import { ListQueryParams } from "@/models/table";
import axios from "axios";

/**
 * Controllers for the Alt Data section.
 *
 * Mirrors `controllers/us-gov-trades.ts`: the list fetchers hand straight to `DataGrid`,
 * which reads its columns from the API response, so a new dataset needs no front-end work.
 */

const BASE = "/interface/api/altdata";

/** Every series ORTEX imports, with coverage counts. The section's landing grid. */
export const getAltDataCatalog = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url } = params;
    const url = next_page_url || `${BASE}/catalog`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** Raw observations. `series` narrows to one dataset; `tradingitem_id` to one company. */
export const getAltDataObservations = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;
    const url = next_page_url || `${BASE}/observations`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...data };

    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** Computed signals — the comparable form, and the more useful grid of the two. */
export const getAltDataSignals = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;
    const url = next_page_url || `${BASE}/signals`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...data };

    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** Coverage counts for the stat cards above the page. */
export const getAltDataSummary = async (signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/summary`, { signal });
    return res.data;
};

/**
 * The Macro section's navigation: its datasets (`bySource`, same shape as the summary's)
 * and its cross-country charts (`views`). Served by the Alt Data API, because it is the
 * same store; only the heading moved.
 */
export const getMacroSummary = async (signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/macro`, { signal });
    return res.data;
};

/**
 * What alternative data exists for one instrument.
 *
 * The company tab calls this before rendering. Most companies will never have alternative
 * data, and a tab that is empty on most stocks is worse than no tab.
 */
export const getAltDataForStock = async (tradingitemId: number, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/stock/${tradingitemId}`, { signal });
    return res.data;
};

/** The company tab's overview: a line per dataset, the company's own facts and its signal moves. */
export const getAltDataStockOverview = async (tradingitemId: number, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/stock/${tradingitemId}/overview`, { signal });
    return res.data;
};

/**
 * Chartable history for one series.
 *
 * Alt data is time series; a grid of periods and values is a line chart the reader has to
 * plot in their head. Returns up to eight lines, chosen by how much history each has.
 */
export const getAltDataTimeseries = async (params: { series?: string; source?: string; tradingitem_id?: number; tradingitem_ids?: string; from_date?: string; to_date?: string }, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/timeseries`, { params, signal });
    return res.data;
};

/**
 * Who can be drawn on a series' chart: every instrument with a reading in it,
 * most history first, with the default eight named. `tradingitem_ids` on the
 * timeseries call is the reader's pick from this list.
 */
export const getAltDataTimeseriesCompanies = async (params: { series?: string; source?: string }, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/timeseries/companies`, { params, signal });
    return res.data;
};

/**
 * One chart across the datasets that measure the same thing.
 *
 * `vehicles` is a line per country's car register plus combined totals. With
 * `tradingitem_id` every line is one company's registrations in that market —
 * Volvo Cars in each register it appears in — and without it each line is the
 * whole market.
 */
export const getAltDataCombined = async (view: string, params: { tradingitem_id?: number; from_date?: string; to_date?: string; mode?: "companies"; tradingitem_ids?: string }, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/combined/${view}`, { params, signal });
    return res.data;
};

/**
 * Who is in a combined view's registers, largest first.
 *
 * The picker behind the per-company chart: ordered by the last twelve closed
 * months of volume across every member, so the first names offered are the
 * ones a reader is most likely to want.
 */
export const getAltDataCombinedCompanies = async (view: string, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/combined/${view}/companies`, { signal });
    return res.data;
};

/**
 * Whether a carmaker payload has been built. `"pending"` means the server has not built it
 * yet (just after a deploy, a non-default window, a company not yet warmed) and has queued
 * the build: the response has the ready shape with empty series (the picker lists only the
 * roster), and the full payload usually follows within a minute or two.
 */
export type CarmakerCoverageStatus = "ready" | "pending";

/**
 * The carmakers the registrations-by-country page can chart: the seven it leads with
 * (Tesla first), then every other company a marque is mapped to.
 */
export const getAltDataCarmakers = async <T = unknown>(signal?: AbortSignal): Promise<{ carmakers: T[]; coverageStatus?: CarmakerCoverageStatus }> => {
    const res = await axios.get(`${BASE}/carmakers`, { signal });
    return res.data;
};

/**
 * One carmaker's new-car registrations by European country, with a Europe total over the
 * registers that are current and the countries that are not covered, and why. Carries
 * `coverageStatus`; a `"pending"` payload has empty series and is worth fetching again.
 */
export const getAltDataCarmakerRegistrations = async (params: { tradingitem_id?: number; ticker?: string; months?: number }, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/carmakers/registrations`, { params, signal });
    return res.data;
};

/**
 * What one dataset is, what it covers, and what has moved recently.
 *
 * Drives the header panel. The section's grids were correct and unreadable: nothing on the
 * page said what a dataset was or whether it covered the company you came looking for.
 */
export const getAltDataDataset = async (sourceSlug: string, signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/dataset/${sourceSlug}`, { signal });
    return res.data;
};

/**
 * The section's front page in one request.
 *
 * Headline coverage plus a card per dataset — what it is, what it reaches, a
 * short stretch of its actual numbers and what moved. One call rather than a
 * summary plus a detail plus a timeseries per dataset, which is three round
 * trips per card on a page whose job is to be quick to read.
 */
export const getAltDataOverview = async (signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/overview`, { signal });
    return res.data;
};

/** Recent alt-data moves with utilisation, cost to borrow, days to cover and days to the next print. */
export const getAltDataPositioning = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;
    const url = next_page_url || `${BASE}/positioning`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...data };
    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** The strongest measured leading indicators per sector, for the overview's sector blocks. */
export const getAltDataLeadBoard = async (signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/lead_board`, { signal });
    return res.data;
};

/** Whether the preview, positioning and lead-board payloads have been built yet. */
export const getAltDataPreviewsStatus = async (signal?: AbortSignal) => {
    const res = await axios.get(`${BASE}/previews/status`, { signal });
    return res.data;
};
